import { useEffect, useState } from 'react'
import { Copy, ExternalLink } from 'lucide-react'
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Textarea,
  useToast,
} from '@/components/ui'
import { useProviderConfig, useSaveProviderConfig } from '@/hooks/useIntegrations'
import type { IntegrationCatalogEntry } from '@/types/integrations'

interface ProviderConfigModalProps {
  adapterId: string
  catalogEntry: IntegrationCatalogEntry
  open: boolean
  onClose: () => void
}

const GOOGLE_CONSOLE_URL = 'https://console.cloud.google.com/apis/credentials'

export function ProviderConfigModal({
  adapterId,
  catalogEntry,
  open,
  onClose,
}: ProviderConfigModalProps) {
  const { addToast } = useToast()
  const { data: cfg, isLoading } = useProviderConfig(adapterId, open)
  const { mutate: save, isPending } = useSaveProviderConfig()

  const [clientId, setClientId] = useState('')
  const [clientSecret, setClientSecret] = useState('')
  const [scopesText, setScopesText] = useState('')
  const [appOwnership, setAppOwnership] = useState('byo')
  const [error, setError] = useState<string | null>(null)

  // Prefill from the loaded config (never the secret). Scopes fall back to the
  // catalog's default_scopes when no override is stored.
  useEffect(() => {
    if (!cfg) return
    setClientId(cfg.oauth_client_id ?? '')
    setAppOwnership(cfg.app_ownership || 'byo')
    const scopes = cfg.oauth_scopes ?? catalogEntry.default_scopes
    setScopesText(scopes.join('\n'))
    setClientSecret('')
  }, [cfg, catalogEntry.default_scopes])

  const secretIsSet = cfg?.oauth_client_secret_set ?? false

  function buildBody(opts: { clearSecret?: boolean } = {}) {
    const scopes = scopesText
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean)
    const body: Record<string, unknown> = {
      app_ownership: appOwnership,
      oauth_client_id: clientId.trim() || null,
      oauth_scopes: scopes.length ? scopes : null,
    }
    if (opts.clearSecret) {
      body.oauth_client_secret = ''
    } else if (clientSecret.length > 0) {
      body.oauth_client_secret = clientSecret
    }
    // else: omit the key entirely → backend keeps the stored secret.
    return body
  }

  function handleSave(clearSecret = false) {
    setError(null)
    save(
      { adapterId, ...buildBody({ clearSecret }) },
      {
        onSuccess() {
          addToast({ type: 'success', title: 'Provider configuration saved' })
          onClose()
        },
        onError(err) {
          setError(err instanceof Error ? err.message : 'Failed to save')
        },
      },
    )
  }

  function copyRedirect() {
    if (catalogEntry.redirect_uri && navigator.clipboard) {
      void navigator.clipboard.writeText(catalogEntry.redirect_uri)
      addToast({ type: 'success', title: 'Redirect URI copied' })
    }
  }

  return (
    <Modal open={open} onOpenChange={(o) => { if (!o) onClose() }}>
      <ModalContent size="md">
        <ModalHeader>
          <ModalTitle>Configure OAuth: {catalogEntry.display_name}</ModalTitle>
        </ModalHeader>
        <ModalBody>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : (
            <div className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {/* Redirect URI to copy into the provider console */}
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">
                  Redirect URI (add this to your OAuth client in Google Cloud Console)
                </label>
                <div className="flex items-center gap-2">
                  <Input readOnly value={catalogEntry.redirect_uri ?? ''} className="font-mono text-xs" />
                  <Button variant="secondary" size="sm" onClick={copyRedirect} title="Copy">
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <a
                  href={GOOGLE_CONSOLE_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 mt-1 text-xs text-primary hover:underline"
                >
                  Open Google Cloud Console credentials <ExternalLink className="w-3 h-3" />
                </a>
              </div>

              {/* Client ID */}
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">
                  Client ID
                </label>
                <Input
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  placeholder="xxxxx.apps.googleusercontent.com"
                />
              </div>

              {/* Client secret (write-only) */}
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">
                  Client secret{' '}
                  {secretIsSet && <Badge variant="success">set</Badge>}
                </label>
                <Input
                  type="password"
                  value={clientSecret}
                  onChange={(e) => setClientSecret(e.target.value)}
                  placeholder={secretIsSet ? 'Enter a new secret to replace…' : 'Enter client secret'}
                />
                {secretIsSet && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSave(true)}
                    className="mt-1 h-auto px-0 py-0 text-xs font-normal text-destructive hover:bg-transparent hover:text-destructive/80"
                  >
                    Clear stored secret
                  </Button>
                )}
              </div>

              {/* Scopes */}
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">
                  Scopes (one per line)
                </label>
                <Textarea
                  rows={3}
                  className="font-mono text-xs"
                  value={scopesText}
                  onChange={(e) => setScopesText(e.target.value)}
                />
              </div>

              {/* App ownership */}
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">
                  App ownership
                </label>
                <select
                  value={appOwnership}
                  onChange={(e) => setAppOwnership(e.target.value)}
                  className="w-full text-sm rounded-md border border-border bg-background px-3 py-1.5 text-foreground focus:outline-hidden focus:ring-2 focus:ring-ring"
                >
                  <option value="byo">Bring your own app (this deployment)</option>
                  <option value="shared">Platform-shared app</option>
                </select>
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" onClick={() => handleSave(false)} isLoading={isPending}>
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
